import { Cancel, ExpandMore, FilterAlt } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import {
    Box,
    Divider,
    FormControlLabel,
    IconButton,
    Menu,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import EddyIconSvg from "SvgIcons/EddyIconSvg";
import theme from "Theme/AppTheme";
import {
    DateRangeFilter,
    DateReferenceFilter,
    HierarchiesFilter,
    PrimaryDriversFilter,
    PrimaryTopicsFilter,
    SafetyEventFilter,
    ConversationLabelingFilter,
    SemanticSearchFilter,
    ThemesFilter,
    TopicsFilter,
    AgentsFilter,
    AgentManagersFilter,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents";
import { LanguageFilter } from "components/ApplicationFilters/FiltersAndChips/Language/LanguageFilter";
import { ModelResultsFilter } from "components/ApplicationFilters/FiltersAndChips/ModelResults/ModelResultsFilter";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSlider from "components/UI/AcxSlider";
import {
    MAX_CALL_DURATION,
    resolveCallDurationSliderState,
} from "components/ApplicationFilters/FiltersAndChips/CallDuration/callDurationSliderState";
import {
    MAX_MESSAGE_COUNT,
    resolveMessageCountSliderState,
} from "components/ApplicationFilters/FiltersAndChips/MessageCount/messageCountSliderState";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import SubscriptionHandler from "./components/Subscriptions/SubscriptionHandler";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import PatientJourneyStagesDialog from "components/UI/PatientJourneyStagesDialog";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { startCase } from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    NavigationType,
    useNavigationType,
    useSearchParams,
} from "react-router-dom";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { useStore } from "utils/useStore";
import ConversationsStore from "../../Stores/ConversationsStore";
import ConversationFilterManagement from "./components/ConversationFilterManagement";
import OutlineFilterInput from "./components/DeepFilter/OutlineFilterInput";
import RBCFilterInput from "./components/DeepFilter/RBCFilterInput";
import { WordsPhrasesConfirmationModal } from "./components/DeepFilter/WordsPhrasesConfirmationModal";
import WordsPhraseInput from "./components/DeepFilter/WordsPhrasesInput";
import FilterTitleWithStatus from "./components/FilterTitleWithStatus";
import ManageFilterInputDrawer, {
    aiFilterCatTitle,
    ConversationFilter,
} from "./components/ManageFilterInputDrawer";
import { SparklingGradientStar } from "SvgIcons/SparklingGradientStar";
import { FilterCheckbox, FilterLabel } from "./shared";
import AgentStore from "../../../KBo/Stores/AgentStore";
import { UserSelectedContext } from "../../../KBo/Models/Context/UserSelectedContext";
import ContextStore from "stores/ContextStore";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import { ModuleScoresFilter } from "components/ApplicationFilters/FiltersAndChips/ModuleScores/ModuleScoresFilter";
import { QuestionResultFilter } from "components/ApplicationFilters/FiltersAndChips/QuestionResult/QuestionResultFilter";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import ConfirmUpdateFilterDialog from "components/UI/ConfirmUpdateFilterDialog";
import { ExtendedMetadataGroupedFilter } from "components/ApplicationFilters/FiltersAndChips/ExtendedMetadata/ExtendedMetadataGroupedFilter";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { SavedFiltersList } from "components/ApplicationFilters/ManageFiltersSignals/ManageFiltersButton";
import SaveFiltersButton from "components/ApplicationFilters/ManageFiltersSignals/SavedFilters/SaveFiltersButton";

const FiltersSectionHeader = ({ children }: { children: React.ReactNode }) => {
    return (
        <Stack sx={{ mt: 2, mb: 1, px: 2 }}>
            <Typography
                sx={{
                    fontWeight: "bold",
                    fontSize: 10,
                    color: theme.palette.gray[400],
                    pb: 0.5,
                    textTransform: "uppercase",
                }}
            >
                {children}
            </Typography>
            <Divider />
        </Stack>
    );
};

const UnfocusedDrawer: React.FunctionComponent = observer(() => {
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const messageStore = useStore(MessageStore);
    const store = useStore(ConversationsStore);
    const contextStore = useStore(ContextStore);
    const currentChatStore = useCurrentChatStore();

    const navigationType = useNavigationType();
    const [searchParams, setSearchParams] = useSearchParams();

    const ref = useRef<any>(null);

    const [isUpdateFiltersLoading, setIsUpdateFiltersLoading] = useState(false);
    const [showConfirmUpdateFilter, setShowConfirmUpdateFilter] =
        useState(false);

    const rootElement = document.getElementById("root");

    const [isSavedFiltersOpen, setIsSavedFiltersOpen] = useState(false);
    const anchorEl = useRef<HTMLElement | null>(null);
    const hasAppliedSubscriptionFilter = useRef(false);

    const fromSignals =
        searchParams.has("fromDomain") &&
        searchParams.get("fromDomain") === "signals" &&
        !!applicationFiltersStore.savedFilterStates["after-drilldown"];

    useEffect(() => {
        // a safety check
        if (!applicationFiltersStore || !messageStore) return;

        const isFocusedConversation = !!store.selectedConversationId;

        if (
            applicationFiltersStore.hasFilterStateChangedSince(
                "Conversations",
            ) &&
            !isFocusedConversation && // dont show filter message on focused conversation
            !fromSignals
        )
            messageStore.logInfo(
                "Results are from your last search. Apply to update.",
            );

        return () => {
            if (!!applicationFiltersStore.savedFilterStates["Conversations"])
                return;
            applicationFiltersStore.saveCurrentFilterState("Conversations");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const afterDrilldownFilters =
            applicationFiltersStore.savedFilterStates["after-drilldown"];
        if (fromSignals && !!afterDrilldownFilters) {
            messageStore.logInfo("Filters automatically applied.");

            applicationFiltersStore.applyExternalFilters(
                afterDrilldownFilters,
                true,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = useCallback(async () => {
        // Removes the Smart Banner upon applying filters
        contextStore.clearBannerContent();
        contextStore.clearContext();
        setBannerFiltersSaved(false);

        applicationFiltersStore.saveCurrentFilterState("Conversations");

        // We don't want to revert to a drilled down state after applying new filters
        delete applicationFiltersStore.savedFilterStates["after-drilldown"];

        // be sure not to reset to ad-hoc filters when navigating away after updating/applying filters
        delete applicationFiltersStore.savedFilterStates["before-drilldown"];

        const valid = await applicationFiltersStore.validateWordsAndPhrases();
        if (!valid)
            return store.messageStore.logError(
                "Invalid Words and Phrases Syntax, please correct the following before continuing: \n" +
                    applicationFiltersStore.wordsAndPhrasesStore
                        .wordsPhrasesFtsSyntaxError,
            );

        const agentStore = store.rootStore.getStore(AgentStore);
        currentChatStore.setUserSelectedContext(
            UserSelectedContext.AppliedFilters,
        );

        if (agentStore.isDrawerOpen) {
            currentChatStore.chat
                .addAgentMessageGroup()
                .addMessage("Filters updated!");
        }

        await store.getSearchConversations();
    }, [applicationFiltersStore, contextStore, currentChatStore, store]);

    const resetFilters = useCallback(async () => {
        delete applicationFiltersStore.savedFilterStates["after-drilldown"];
        applicationFiltersStore.resetAllFilters();
        store.updateAllTemporaryFilters();
        await applyFilters();
    }, [applicationFiltersStore, applyFilters, store]);

    const setFilterFromSaved = useCallback(
        async (savedFilterId: string) => {
            await resetFilters();
            await applicationFiltersStore.applySavedFilters(savedFilterId);
        },
        [resetFilters, applicationFiltersStore],
    );

    useEffect(() => {
        const subscriptionId = searchParams.get("subscriptionId");

        if (!subscriptionId || hasAppliedSubscriptionFilter.current) return;

        const savedFilter = applicationFiltersStore.savedFilters.find(
            (filter) =>
                filter.applicationFilterSubscriptionId === subscriptionId,
        );

        if (!savedFilter) return;

        const applyGivenFilter = async () => {
            await setFilterFromSaved(savedFilter.id);
            const startDateParam = searchParams.get("startDate");
            const endDateParam = searchParams.get("endDate");

            if (startDateParam) {
                const startDate = moment(
                    startDateParam.trim(),
                    "YYYY-MM-DD",
                    true,
                );
                const endDate = endDateParam
                    ? moment(endDateParam.trim(), "YYYY-MM-DD", true)
                    : startDate.clone();

                if (startDate.isValid() && endDate.isValid()) {
                    const rangeStart = startDate.clone().startOf("day");
                    const rangeEnd = endDate.clone().endOf("day");
                    applicationFiltersStore.quickFiltersStore.setDateRangeLabel(
                        "",
                    );
                    applicationFiltersStore.quickFiltersStore.setDatePair(
                        rangeStart,
                        rangeEnd,
                    );
                    applicationFiltersStore.setDateRangeLabel("");
                    applicationFiltersStore.setDatePair(rangeStart, rangeEnd);
                }
            }

            applicationFiltersStore.saveCurrentFilterState("Update");
            await applyFilters();
            store.messageStore.logMessage(
                "Filters from your subscription email have been applied to this view.",
                "info",
            );
            const updatedParams = new URLSearchParams(searchParams);
            updatedParams.delete("subscriptionId");
            updatedParams.delete("startDate");
            updatedParams.delete("endDate");
            setSearchParams(updatedParams, { replace: true });
        };

        hasAppliedSubscriptionFilter.current = true;
        applyGivenFilter();
    }, [
        searchParams,
        applicationFiltersStore.savedFilters.length,
        applicationFiltersStore,
        applyFilters,
        store,
        setFilterFromSaved,
        setSearchParams,
    ]);

    const onInitialLoad = async () => {
        applicationFiltersStore.wordsAndPhrasesStore.setWordsPhrasesFtsSyntaxError(
            "",
        );

        const fromAgent = searchParams.has("agent");
        if (fromAgent) {
            await store.defaultLocalStorage
                .getItem<ApplicationFilters>("agentConversationsFilters")
                .then((filters) => {
                    if (!filters || !!filters["conversationIds"]) return;
                    // agent filters handle date reference as an enum, FE expects a string
                    const dateReference = filters["dateReference"] as number;
                    if (dateReference === 0)
                        filters.dateReferenceOption =
                            DateReferenceOption.InteractionDate;
                    if (dateReference === 1)
                        filters.dateReferenceOption =
                            DateReferenceOption.ArrivalDate;

                    applicationFiltersStore.applyExternalFilters(filters, true);
                });
        }

        store.setIsInitialLoad(true, fromAgent);
        store.setPageNumberAndGetConversations(1);
    };

    const renderFilter = (filterItem: ConversationFilter) => {
        switch (filterItem.storeObjectKey) {
            case "dateRange":
                return (
                    <DateRangeFilter
                        store={applicationFiltersStore.quickFiltersStore}
                        filterKey="dateRange"
                        maxWidth="100%"
                        inputStyle={{ width: "100%" }}
                        wrapperStyle={{ maxWidth: "100%" }}
                        onSelect={() =>
                            applicationFiltersStore.alertOfQuickFilterOverride(
                                "dateRange",
                            )
                        }
                        onDateTypeChange={() =>
                            applicationFiltersStore.alertOfQuickFilterOverride(
                                "dateReferenceOption",
                            )
                        }
                    />
                );
            case "callDuration":
                return (
                    <AcxSlider
                        description="minutes"
                        // A range reaching the top of the track reads as "20+ min". One with no
                        // stored upper bound at all has no literal thumb position, and drawn raw it
                        // reads as "Any Duration", claiming no filter while one is applied.
                        {...resolveCallDurationSliderState(
                            applicationFiltersStore.callDuration,
                            { maxBound: MAX_CALL_DURATION },
                        )}
                        onChange={(duration) => {
                            applicationFiltersStore.setCallDuration(duration);
                        }}
                        maxValue={MAX_CALL_DURATION}
                        noValueLabel="Any Duration"
                        descriptionSx={{
                            fontSize: "12px",
                            color: theme.palette.gray[500],
                        }}
                        sx={{ py: 0.5 }}
                    />
                );
            case "messageCount":
                return (
                    <AcxSlider
                        description="messages"
                        // A range reaching the end of the track means "and over", and reads that way
                        // rather than as a pair of numbers, which is also what a drilldown out of
                        // the last message count bucket sets.
                        {...resolveMessageCountSliderState(
                            applicationFiltersStore.messageCount,
                            { maxBound: MAX_MESSAGE_COUNT },
                        )}
                        onChange={(messageCount) => {
                            applicationFiltersStore.setMessageCount(
                                messageCount,
                            );
                        }}
                        maxValue={MAX_MESSAGE_COUNT}
                        noValueLabel="Any Count"
                        descriptionSx={{
                            fontSize: "12px",
                            color: theme.palette.gray[500],
                        }}
                        sx={{ py: 0.5 }}
                    />
                );
            case "hierarchy":
                return (
                    <HierarchiesFilter
                        store={applicationFiltersStore.quickFiltersStore}
                        filterKey="hierarchyIds"
                        setHierarchyIds={() =>
                            applicationFiltersStore.alertOfQuickFilterOverride(
                                "hierarchyIds",
                            )
                        }
                        displayType="input"
                        fixedPopper
                        anchorEl={rootElement}
                    />
                );
            case "classifiers":
                return (
                    <RBCFilterInput
                        store={applicationFiltersStore.rbcFilterStore}
                    />
                );
            case "wordsOrPhrases":
                return (
                    <WordsPhraseInput
                        store={applicationFiltersStore.wordsAndPhrasesStore}
                    />
                );
            case "evaluationType":
                return (
                    <Stack>
                        <FormControlLabel
                            label="No Evaluation"
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-eval-type-no-evaluation"
                                    onChange={() => {
                                        applicationFiltersStore.toggleEvaluationType(
                                            EvaluationType.NotEvaluated,
                                        );
                                    }}
                                    checked={applicationFiltersStore.evaluationTypes.includes(
                                        EvaluationType.NotEvaluated,
                                    )}
                                />
                            }
                        />
                        <FormControlLabel
                            label={
                                <Stack direction="row" gap={0.5}>
                                    AI Evaluated
                                    <CheckCircleOutlineIcon
                                        color={"primary"}
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                        }}
                                    />
                                </Stack>
                            }
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-eval-type-AI-evaluated"
                                    onChange={() => {
                                        applicationFiltersStore.toggleEvaluationType(
                                            EvaluationType.AIEvaluated,
                                        );
                                    }}
                                    checked={applicationFiltersStore.evaluationTypes.includes(
                                        EvaluationType.AIEvaluated,
                                    )}
                                />
                            }
                            disabled={applicationFiltersStore.evaluationTypes.includes(
                                EvaluationType.NotEvaluated,
                            )}
                        />
                        <FormControlLabel
                            label={
                                <Stack direction="row" gap={0.5}>
                                    Human Evaluated
                                    <CheckCircleOutlineIcon
                                        color={"secondary"}
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            float: "right",
                                        }}
                                    />
                                </Stack>
                            }
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-eval-type-human-evaluated"
                                    onChange={() => {
                                        applicationFiltersStore.toggleEvaluationType(
                                            EvaluationType.HumanEvaluated,
                                        );
                                    }}
                                    checked={applicationFiltersStore.evaluationTypes.includes(
                                        EvaluationType.HumanEvaluated,
                                    )}
                                />
                            }
                            disabled={applicationFiltersStore.evaluationTypes.includes(
                                EvaluationType.NotEvaluated,
                            )}
                        />
                    </Stack>
                );
            case "conversationLabeling":
                return (
                    <ConversationLabelingFilter
                        store={applicationFiltersStore}
                        filterKey="dataLabels"
                    />
                );
            case "mediaType":
                return (
                    <Stack>
                        <FormControlLabel
                            label={"Calls"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-media-type-calls"
                                    onChange={() => {
                                        applicationFiltersStore.toggleMediaType(
                                            MediaType.Audio,
                                        );
                                    }}
                                    checked={applicationFiltersStore.mediaTypes.includes(
                                        MediaType.Audio,
                                    )}
                                />
                            }
                        />
                        <FormControlLabel
                            label={"Chats"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-media-type-chats"
                                    onChange={() => {
                                        applicationFiltersStore.toggleMediaType(
                                            MediaType.ProcessedChat,
                                        );
                                    }}
                                    checked={applicationFiltersStore.mediaTypes.includes(
                                        MediaType.ProcessedChat,
                                    )}
                                />
                            }
                        />
                    </Stack>
                );
            case "callDirection":
                return (
                    <Stack>
                        <FormControlLabel
                            label={"Inbound"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-call-direction-inbound"
                                    onChange={() => {
                                        applicationFiltersStore.toggleCallDirection(
                                            CallDirection.Inbound,
                                        );
                                    }}
                                    checked={applicationFiltersStore.callDirections.includes(
                                        CallDirection.Inbound,
                                    )}
                                />
                            }
                        />
                        <FormControlLabel
                            label={"Outbound"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-call-direction-outbound"
                                    onChange={() => {
                                        applicationFiltersStore.toggleCallDirection(
                                            CallDirection.Outbound,
                                        );
                                    }}
                                    checked={applicationFiltersStore.callDirections.includes(
                                        CallDirection.Outbound,
                                    )}
                                />
                            }
                        />
                        <FormControlLabel
                            label={"Transfer"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-call-direction-transfer"
                                    onChange={() => {
                                        applicationFiltersStore.toggleCallDirection(
                                            CallDirection.Transfer,
                                        );
                                    }}
                                    checked={applicationFiltersStore.callDirections.includes(
                                        CallDirection.Transfer,
                                    )}
                                />
                            }
                        />
                        <FormControlLabel
                            label={"Other"}
                            control={
                                <FilterCheckbox
                                    id="conversations-filters-call-direction-other"
                                    onChange={() => {
                                        applicationFiltersStore.toggleCallDirection(
                                            CallDirection.Other,
                                        );
                                    }}
                                    checked={applicationFiltersStore.callDirections.includes(
                                        CallDirection.Other,
                                    )}
                                />
                            }
                        />
                    </Stack>
                );
            case "clientCallId":
                return (
                    <AcxMainTextField
                        id="conversations-filters-Client-Call-ID"
                        type="text"
                        value={applicationFiltersStore.clientCallId}
                        onChange={(e) =>
                            applicationFiltersStore.setClientCallId(
                                e.target.value,
                            )
                        }
                        endAdornment={
                            <Tooltip title="This field supports comma separated values e.g.: value1,value2,value3 ">
                                <InfoIcon color="disabled" />
                            </Tooltip>
                        }
                        multiline
                        multilinePadding
                        rowsMin={1}
                    />
                );
            case "agents":
                return (
                    <AgentsFilter
                        filterKey="agentIds"
                        store={applicationFiltersStore}
                    />
                );
            case "agentManagers":
                return (
                    <AgentManagersFilter
                        filterKey="agentManagerIds"
                        store={applicationFiltersStore}
                    />
                );
            case "eddyEffectSignal":
                return (
                    <AcxToggleButton
                        id="conversations-filters-eddy-effect"
                        exclusive
                        items={[
                            {
                                id: "conversations-filters-eddy-effect-is-present",
                                element: "Is Present",
                                value: true,
                            },
                            {
                                id: "conversations-filters-eddy-effect-is-not-present",
                                element: "Is Not Present",
                                value: false,
                            },
                        ]}
                        onChange={(value?: boolean) => {
                            applicationFiltersStore.setEddyEffectStatus(value);
                        }}
                        defaultValue={applicationFiltersStore.eddyEffectStatus}
                        justifyContent="center"
                        groupFlexGrow={1}
                    />
                );

            case "startingSentiment":
                return (
                    <AcxToggleButton
                        id="conversations-filters-starting-sentiment"
                        exclusive
                        items={[
                            {
                                id: "conversations-filters-starting-sentiment-negative",
                                element: "Negative",
                                value: Sentiment.Negative,
                            },
                            {
                                id: "conversations-filters-starting-sentiment-neutral",
                                element: "Neutral",
                                value: Sentiment.Neutral,
                            },
                            {
                                id: "conversations-filters-starting-sentiment-positive",
                                element: "Positive",
                                value: Sentiment.Positive,
                            },
                        ]}
                        onChange={(value) => {
                            applicationFiltersStore.setBeginningSentiment(
                                value,
                            );
                        }}
                        defaultValue={
                            applicationFiltersStore.beginningSentiment
                        }
                        justifyContent="center"
                        groupFlexGrow={1}
                    />
                );
            case "contactType":
                return (
                    <AcxSelectMulti
                        id="conversations-filters-contact-type"
                        fullWidth
                        options={
                            applicationFilterValuesStore.contactTypeOptions
                        }
                        defaultValue={applicationFiltersStore.contactTypes.map(
                            (i) => ({
                                label: applicationFilterValuesStore.getContactTypeLabel(
                                    i,
                                ),
                                value: i,
                            }),
                        )}
                        onChange={(options) => {
                            applicationFiltersStore.setContactTypes(
                                !!options ? options : [],
                            );
                        }}
                        valueField="value"
                        labelField="label"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                );
            case "patientJourney":
                return (
                    <AcxSelectMulti
                        id="conversations-filters-patient-journey-stage"
                        fullWidth
                        options={applicationFiltersStore.journeyStageOptions}
                        defaultValue={applicationFiltersStore.patientJourneyStages.map(
                            (i) => ({
                                label: applicationFiltersStore.journeyStageOptions.find(
                                    (stage) => stage.value === i,
                                )?.label as string,
                                value: i,
                            }),
                        )}
                        onChange={(options) => {
                            applicationFiltersStore.setPatientJourneyStages(
                                !!options ? options : [],
                            );
                        }}
                        valueField="value"
                        labelField="label"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                );
            case "outline":
                return (
                    <OutlineFilterInput
                        store={applicationFiltersStore.outlineFilterStore}
                    />
                );
            case "modelResults":
                return (
                    <ModelResultsFilter
                        filterKey="modelResults"
                        store={applicationFiltersStore}
                        variant="conversations"
                    />
                );
            case "moduleScores":
                return (
                    <ModuleScoresFilter
                        filterKey="moduleScores"
                        store={applicationFiltersStore}
                    />
                );
            case "questionResults":
                return (
                    <QuestionResultFilter
                        filterKey="questionResults"
                        store={applicationFiltersStore}
                        variant="conversations"
                    />
                );
            case "primaryTheme":
                return (
                    <TopicsFilter
                        filterKey="topics"
                        store={applicationFiltersStore}
                    />
                );
            case "themes":
                return (
                    <ThemesFilter
                        filterKey="themes"
                        store={applicationFiltersStore}
                    />
                );
            case "primaryTopic":
                return (
                    <PrimaryTopicsFilter
                        filterKey="primaryTopics"
                        store={applicationFiltersStore}
                        id="conversation-explorer-filters-primary-topic"
                    />
                );
            case "primaryDriver":
                return (
                    <PrimaryDriversFilter
                        filterKey="drivers"
                        store={applicationFiltersStore}
                        id="conversation-explorer-filters-primary-driver"
                    />
                );
            case "hipaaCompliance":
                return (
                    <AcxToggleButton
                        id="conversations-filters-HIPAA-Compliance"
                        exclusive
                        items={[
                            {
                                id: "conversations-filters-HIPAA-Compliance-Not-Compliant",
                                element: "Not Compliant",
                                value: HipaaCompliance.NotCompliant,
                            },
                            {
                                id: "conversations-filters-HIPAA-Compliance-Compliant",
                                element: "Compliant",
                                value: HipaaCompliance.Compliant,
                            },
                            {
                                id: "conversations-filters-HIPAA-Compliance-NA",
                                element: "N/A",
                                value: HipaaCompliance.NotApplicable,
                            },
                        ]}
                        onChange={(value) => {
                            applicationFiltersStore.setHipaaCompliance(value);
                        }}
                        defaultValue={applicationFiltersStore.hipaaCompliance}
                        justifyContent="center"
                        groupFlexGrow={1}
                    />
                );
            case "endingSentiment":
                return (
                    <AcxToggleButton
                        id="conversations-filters-ending-sentiment"
                        exclusive
                        items={[
                            {
                                id: "conversations-filters-ending-sentiment-negative",
                                element: "Negative",
                                value: Sentiment.Negative,
                            },
                            {
                                id: "conversations-filters-ending-sentiment-neutral",
                                element: "Neutral",
                                value: Sentiment.Neutral,
                            },
                            {
                                id: "conversations-filters-ending-sentiment-positive",
                                element: "Positive",
                                value: Sentiment.Positive,
                            },
                        ]}
                        onChange={(value) => {
                            applicationFiltersStore.setEndingSentiment(value);
                        }}
                        defaultValue={applicationFiltersStore.endingSentiment}
                        justifyContent="center"
                        groupFlexGrow={1}
                    />
                );
            case "safetyEvent":
                return (
                    <SafetyEventFilter
                        filterKey={"safetyEvent"}
                        store={applicationFiltersStore}
                    />
                );

            case "meta1":
                return (
                    <AcxMainTextField
                        type="text"
                        id={`conversations-meta-filter-1`}
                        value={applicationFiltersStore.meta1}
                        onChange={(e) =>
                            applicationFiltersStore.setMeta1(e.target.value)
                        }
                    />
                );
            case "meta2":
                return (
                    <AcxMainTextField
                        type="text"
                        id={`conversations-meta-filter-2`}
                        value={applicationFiltersStore.meta2}
                        onChange={(e) =>
                            applicationFiltersStore.setMeta2(e.target.value)
                        }
                    />
                );
            case "meta3":
                return (
                    <AcxMainTextField
                        type="text"
                        id={`conversations-meta-filter-3`}
                        value={applicationFiltersStore.meta3}
                        onChange={(e) =>
                            applicationFiltersStore.setMeta3(e.target.value)
                        }
                    />
                );
            case "meta4":
                return (
                    <AcxMainTextField
                        type="text"
                        id={`conversations-meta-filter-4`}
                        value={applicationFiltersStore.meta4}
                        onChange={(e) =>
                            applicationFiltersStore.setMeta4(e.target.value)
                        }
                    />
                );
            case "meta5":
                return (
                    <AcxMainTextField
                        type="text"
                        id={`conversations-meta-filter-5`}
                        value={applicationFiltersStore.meta5}
                        onChange={(e) =>
                            applicationFiltersStore.setMeta5(e.target.value)
                        }
                    />
                );
            case "favoriteConversations":
                return (
                    <Stack direction="row" gap={1} alignItems={"center"}>
                        <AcxSwitch
                            id="conversations-filters-favorite-conversations"
                            checked={
                                applicationFiltersStore.favoriteConversations ??
                                false
                            }
                            onChange={() =>
                                applicationFiltersStore.toggleFavoriteConversationsFilter()
                            }
                            sx={{ marginLeft: 0 }}
                        />
                        <Typography>
                            Show favorite conversations only
                        </Typography>
                    </Stack>
                );
            case "dateReferenceOption":
                return (
                    <DateReferenceFilter
                        filterKey="dateReferenceOption"
                        store={applicationFiltersStore.quickFiltersStore}
                    />
                );
            case "semanticSearch":
                return (
                    <SemanticSearchFilter
                        id="conversations-ai-powered-search-input"
                        store={applicationFiltersStore}
                        filterKey="embeddedTranscriptionQuery"
                    />
                );
            case "language":
                return (
                    <LanguageFilter
                        id="application-filters-language-filter"
                        filterKey="languages"
                        store={applicationFiltersStore}
                    ></LanguageFilter>
                );
            default:
                // extendedMetadata grouped and ungrouped filters
                const extendedMetadataField =
                    applicationFilterItemsStore.extendedMetadataFields.find(
                        (item) => item.name === filterItem.storeObjectKey,
                    );
                const isGroupable = extendedMetadataField?.isGroupable;
                return (
                    <>
                        {isGroupable ? (
                            <ExtendedMetadataGroupedFilter
                                filterKey={
                                    filterItem.filterKey ??
                                    `extendedMetadata.${filterItem.storeObjectKey}`
                                }
                                store={applicationFiltersStore}
                                classifierId={extendedMetadataField?.id ?? ""}
                            />
                        ) : (
                            <AcxMainTextField
                                id={
                                    "conversations-extMetaItem-input" +
                                    filterItem.filterTitle
                                }
                                type="text"
                                value={
                                    applicationFiltersStore.extendedMetadata?.[
                                        filterItem.filterTitle
                                    ]
                                }
                                onChange={(e) => {
                                    applicationFiltersStore.setExtendedMetadataPair(
                                        filterItem.filterTitle,
                                        e.target.value,
                                    );
                                }}
                                endAdornment={
                                    <Tooltip title="This field supports comma separated values e.g.: value1,value2,value3 ">
                                        <InfoIcon color="disabled" />
                                    </Tooltip>
                                }
                            />
                        )}
                    </>
                );
        }
    };

    const getFilterTitle = (filterItem: ConversationFilter) => {
        const title = filterItem.filterLabel ?? filterItem.filterTitle;
        switch (filterItem.storeObjectKey) {
            case "dateRange":
                return (
                    startCase(
                        applicationFiltersStore.quickFiltersStore
                            .dateReferenceOption,
                    ) + " Range"
                );
            case "mediaType":
                return filterItem.filterTitle;
            case "eddyEffectSignal":
                return (
                    <>
                        {title}{" "}
                        <EddyIconSvg
                            style={{ width: "16px", height: "16px" }}
                        />
                    </>
                );
            case "patientJourney":
                return (
                    <>
                        {title} <PatientJourneyStagesDialog />
                    </>
                );
            default:
                return (
                    <FilterTitleWithStatus filterKey={filterItem.filterKey}>
                        {title}
                    </FilterTitleWithStatus>
                );
        }
    };

    const renderedShownFilters = store.shownFilterItems.map((category) => {
        if (!category.filters.length) return null;

        const renderedFilters = category.filters.map((filterItem) => {
            const shouldHideTitle = filterItem.storeObjectKey.match(
                /(wordsOrPhrases|classifiers|outline|modelResults|moduleScores|questionResults)/,
            );
            return (
                <Stack sx={{ px: 2, py: 1 }} key={filterItem.id}>
                    {!shouldHideTitle && (
                        <FilterLabel>{getFilterTitle(filterItem)}</FilterLabel>
                    )}
                    {renderFilter(filterItem)}
                </Stack>
            );
        });

        return (
            <React.Fragment key={category.title}>
                <FiltersSectionHeader>
                    {category.title}{" "}
                    {category.title === aiFilterCatTitle && (
                        <SparklingGradientStar />
                    )}
                </FiltersSectionHeader>
                {renderedFilters}
            </React.Fragment>
        );
    });

    React.useEffect(() => {
        if (
            store.estimatedConversationCount &&
            navigationType === NavigationType.Pop
        )
            return;

        if (!store.estimatedConversationCount) {
            onInitialLoad();
        } else if (fromSignals) {
            store.getSearchConversations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const appliedFilterCount =
        ApplicationFiltersInstanceStore.getAppliedFilterCount(
            applicationFiltersStore.toFilterObject(),
        );

    const [bannerFiltersSaved, setBannerFiltersSaved] = React.useState(false);
    React.useEffect(() => {
        if (
            !store.anyTaskLoading &&
            !bannerFiltersSaved &&
            contextStore.bannerExists
        ) {
            applicationFiltersStore.saveCurrentFilterState("banner-filters");
            setBannerFiltersSaved(true);
        }
    }, [
        store.anyTaskLoading,
        bannerFiltersSaved,
        contextStore.bannerExists,
        applicationFiltersStore,
    ]);

    const handleClickUpdateFilter = () => {
        const storageKey = `hasConfirmChangePinnedFilterModalShown-${applicationFiltersStore.selectedSavedFilter?.id}`;

        const filterHasAnySubscriptions =
            applicationFiltersStore.selectedSavedFilter
                ?.hasDashboardSubscription ||
            !!applicationFiltersStore.selectedSavedFilter
                ?.applicationFilterSubscriptionId ||
            applicationFiltersStore.selectedSavedFilter
                ?.hasOtherApplicationFilterSubscription;

        const showConfirmPinChange =
            filterHasAnySubscriptions && !sessionStorage.getItem(storageKey);
        if (showConfirmPinChange) {
            setShowConfirmUpdateFilter(true);
            sessionStorage.setItem(storageKey, "1");
        } else {
            handleUpdateSavedFilter();
        }
    };

    const handleUpdateSavedFilter = async () => {
        const success =
            await applicationFiltersStore.updateSelectedSavedFilterWith(
                applicationFiltersStore,
                applicationFilterValuesStore,
            );
        applyFilters();

        if (!success)
            return messageStore.logError(
                "Failed to update filter. Please try again.",
            );

        messageStore.logInfo(
            `'${
                applicationFiltersStore.selectedSavedFilter!.name
            }' successfully updated.`,
        );
    };

    return (
        <>
            <Stack
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRight: `1px solid ${theme.palette.gray[200]}`,
                }}
            >
                <Stack
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        justifyContent: "center",
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.gray[200]}`,
                        boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
                        width: "100%",
                        gap: 2,
                        mt: 2,
                    }}
                    ref={ref}
                >
                    <Stack
                        sx={{
                            marginTop: theme.spacing(1),
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexDirection: "row",
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "18px",
                                fontWeight: "bold",
                            }}
                        >
                            Search Filters
                        </Typography>
                        <Box
                            ref={(el: HTMLElement | null) =>
                                (anchorEl.current = el)
                            }
                        >
                            <AcxButton
                                id="conversations-filters-button"
                                variant="outlined"
                                color="white"
                                onClick={() =>
                                    setIsSavedFiltersOpen((open) => !open)
                                }
                                sx={{ px: 1 }}
                            >
                                <FilterAlt
                                    fontSize="small"
                                    sx={(theme) => ({
                                        marginRight: theme.spacing(1),
                                        color: theme.palette.gray[600],
                                    })}
                                />
                                <Typography
                                    sx={(theme) => ({
                                        maxWidth: "150px",
                                        overflowX: "hidden",
                                        textOverflow: "ellipsis",
                                        color: theme.palette.gray[600],
                                        fontSize: "14px",
                                        lineHeight: "20px",
                                        textAlign: "center",
                                    })}
                                >
                                    {!!applicationFiltersStore.selectedSavedFilter
                                        ? applicationFiltersStore
                                              .selectedSavedFilter.name
                                        : "Saved Filters"}
                                </Typography>
                                {!!applicationFiltersStore.selectedSavedFilter && (
                                    <IconButton
                                        size="small"
                                        sx={{
                                            fontSize: "16px",
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            resetFilters();
                                        }}
                                    >
                                        <Cancel
                                            fontSize="inherit"
                                            htmlColor={theme.palette.gray[300]}
                                        />
                                    </IconButton>
                                )}
                                <ExpandMore
                                    fontSize="inherit"
                                    sx={(theme) => ({
                                        fontSize: "20px",
                                        color: theme.palette.gray[400],
                                    })}
                                />
                            </AcxButton>
                            <Menu
                                open={isSavedFiltersOpen}
                                onClose={() => setIsSavedFiltersOpen(false)}
                                anchorEl={anchorEl.current}
                                anchorReference="anchorEl"
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                transformOrigin={{
                                    vertical: -8,
                                    horizontal: "right",
                                }}
                                slotProps={{
                                    paper: {
                                        sx: (theme) => ({
                                            padding: theme.spacing(1),
                                            borderRadius: 1,
                                            minWidth: "164px",
                                        }),
                                    },
                                }}
                            >
                                <SavedFiltersList
                                    onSelect={async (filter) => {
                                        setIsSavedFiltersOpen(false);
                                        await setFilterFromSaved(filter.id);
                                        store.updateAllTemporaryFilters();
                                        applicationFiltersStore.saveCurrentFilterState(
                                            "Update",
                                        );

                                        const agentStore =
                                            store.rootStore.getStore(
                                                AgentStore,
                                            );
                                        currentChatStore.setUserSelectedContext(
                                            UserSelectedContext.AppliedFilters,
                                        );

                                        if (agentStore.isDrawerOpen) {
                                            currentChatStore.chat
                                                .addAgentMessageGroup()
                                                .addMessage("Filters updated!");
                                        }

                                        applyFilters();
                                    }}
                                >
                                    <SavedFiltersList.Action
                                        id="conversations-filters-manage-saved-filters"
                                        onClick={() => {
                                            store.setUnsavedFilters([
                                                ...applicationFiltersStore.ownedSavedFilters,
                                            ]);
                                            store.manageSavedFilterDialogStore.open();
                                        }}
                                    >
                                        Manage Filters
                                    </SavedFiltersList.Action>
                                </SavedFiltersList>
                            </Menu>
                        </Box>
                    </Stack>
                    <Divider sx={{ borderBottomWidth: "2px" }} />
                    <Stack
                        sx={{
                            width: "100%",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexDirection: "row",
                            minHeight: "30px",
                            gap: 0.5,
                        }}
                    >
                        <Typography sx={{ textWrap: "nowrap" }}>
                            {appliedFilterCount}
                            {appliedFilterCount === 1
                                ? " Filter "
                                : " Filters "}
                            Applied
                        </Typography>
                        <Stack sx={{ flexDirection: "row", gap: 0.5 }}>
                            {(appliedFilterCount > 0 ||
                                !!applicationFiltersStore.selectedSavedFilter) && (
                                <AcxButton
                                    fullWidth
                                    id="conversations-filters-clear"
                                    size="small"
                                    onClick={() => {
                                        resetFilters();
                                    }}
                                    color="white"
                                    sx={(theme) => ({
                                        paddingInline: theme.spacing(2),
                                        paddingBlock: theme.spacing(1),
                                    })}
                                >
                                    Clear
                                </AcxButton>
                            )}
                            {!!applicationFiltersStore.selectedSavedFilter &&
                                applicationFiltersStore.isSelectedSavedFilterEditable && (
                                    <AcxButton
                                        fullWidth
                                        id="conversations-filters-update"
                                        size="small"
                                        color="white"
                                        sx={(theme) => ({
                                            paddingInline: theme.spacing(2),
                                            paddingBlock: theme.spacing(1),
                                        })}
                                        disabled={
                                            !applicationFiltersStore.hasChangedSinceLastUpdate
                                        }
                                        loading={applicationFiltersStore.getTaskLoading(
                                            ApplicationFiltersStore.Tasks
                                                .UPDATE_FILTER,
                                        )}
                                        onClick={handleClickUpdateFilter}
                                    >
                                        Update
                                    </AcxButton>
                                )}
                            {(applicationFiltersStore.selectedSavedFilter ||
                                appliedFilterCount > 0) && (
                                <SaveFiltersButton
                                    id={
                                        applicationFiltersStore.selectedSavedFilter
                                            ? "conversations-filters-save-as-new"
                                            : "conversations-filters-save"
                                    }
                                    variant="menu"
                                    tooltip="Save filter as template"
                                    showVisibilityToggle
                                    disabled={
                                        applicationFiltersStore
                                            .wordsAndPhrasesStore.hasError
                                    }
                                    sx={(theme) => ({
                                        paddingInline: theme.spacing(2),
                                        paddingBlock: theme.spacing(1),
                                    })}
                                    onSave={applyFilters}
                                >
                                    {applicationFiltersStore.selectedSavedFilter
                                        ? "Save as New"
                                        : "Save"}
                                </SaveFiltersButton>
                            )}
                        </Stack>
                    </Stack>
                    <SubscriptionHandler
                        conversationStore={store}
                        applicationFiltersStore={applicationFiltersStore}
                        applicationFilterValuesStore={
                            applicationFilterValuesStore
                        }
                        messageStore={messageStore}
                    />
                </Stack>

                <Stack
                    sx={{
                        overflowY: "auto",
                        flex: 1,
                    }}
                >
                    {renderedShownFilters}
                </Stack>

                <Stack
                    sx={{
                        flexDirection: "row",
                        p: 2,
                        borderTop: `1px solid ${theme.palette.gray[200]}`,
                        width: "100%",
                        gap: 1,
                    }}
                >
                    <AcxButton
                        id="conversations-filter-toggle-manage-filter-button"
                        data-testid="conversations-filter-toggle-manage-filter-button"
                        onClick={() => {
                            store.toggleManageFilterInput();
                        }}
                        loading={store.anyTaskLoading}
                        color="primary"
                        variant="text"
                        fullWidth
                    >
                        + Manage Filters
                    </AcxButton>
                    <AcxButton
                        id="conversations-filter-cancel-button"
                        onClick={() => {
                            store.cancelConversationsRequests();
                        }}
                        color="white"
                        fullWidth
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton
                        id="conversations-filter-apply-filters-button"
                        data-testid="conversations-filter-apply-filters-button"
                        color="primary"
                        onClick={applyFilters}
                        loading={store.anyTaskLoading}
                        disabled={
                            applicationFiltersStore.hasError ||
                            applicationFiltersStore.wordsAndPhrasesStore
                                .hasError ||
                            (bannerFiltersSaved &&
                                contextStore.bannerExists &&
                                !applicationFiltersStore.hasFilterStateChangedSince(
                                    "banner-filters",
                                ))
                        }
                        fullWidth
                    >
                        Apply
                    </AcxButton>
                </Stack>
            </Stack>

            <WordsPhrasesConfirmationModal
                store={applicationFiltersStore.wordsAndPhrasesStore}
            />
            <AcxDialog
                title="Manage Saved Filters"
                isOpen={store.manageSavedFilterDialogStore.isOpen}
                onClose={() => {
                    store.manageSavedFilterDialogStore.close();
                }}
                dialogContentChildren={<ConversationFilterManagement />}
                maxWidth="md"
                contentWidth="800px"
                children={
                    <Tooltip
                        title={
                            store.canSaveFilters
                                ? "Filter names must be unique"
                                : ""
                        }
                    >
                        <Box>
                            <AcxButton
                                fullWidth
                                id="conversations-manage-filter-panel-save-changes-button"
                                onClick={store.manageSavedFilterCDStore.open}
                                color="primary"
                                disabled={store.canSaveFilters}
                            >
                                Save Changes
                            </AcxButton>
                        </Box>
                    </Tooltip>
                }
            />
            <AcxDialog
                title="Confirm Save Filters"
                text="Deleting/Editing filters is immediate and permanent. Are you sure you want to save these changes?"
                isOpen={store.manageSavedFilterCDStore.isOpen}
                onClose={store.manageSavedFilterCDStore.close}
                contentWidth="400px"
                children={
                    <Box>
                        <AcxButton
                            fullWidth
                            id="conversations-confirm-save-changes-dialog-confirm-button"
                            color="primary"
                            loading={isUpdateFiltersLoading}
                            onClick={async () => {
                                setIsUpdateFiltersLoading(true);

                                await store.saveUnsavedFilters();

                                store.manageSavedFilterCDStore.close();
                                store.manageSavedFilterDialogStore.close();
                                setIsUpdateFiltersLoading(false);
                            }}
                        >
                            Confirm Save Changes
                        </AcxButton>
                    </Box>
                }
                hideCloseButton
                textAlignCenterContent
                textAlignCenterTitle
            />
            <AcxDrawer
                anchor={"left"}
                open={store.filterInputManagerIsOpen}
                collapsible={"full"}
                content={<ManageFilterInputDrawer />}
                offsetPosition="Left"
                offsetPx={ref.current ? ref.current.offsetWidth + 15 : 0}
                customRootStyles={{
                    zIndex: 2,
                    whiteSpace: "pre-wrap",
                }}
                onDrawerClose={store.toggleManageFilterInput}
                disableEnforceFocus
            />
            <ConfirmUpdateFilterDialog
                showConfirmUpdateFilter={showConfirmUpdateFilter}
                setShowConfirmUpdateFilter={setShowConfirmUpdateFilter}
                handleConfirm={handleUpdateSavedFilter}
            />
        </>
    );
});

export default UnfocusedDrawer;
